import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, StatCard } from '../../components/ui/Page';
import { ago } from '../../lib/format';

type RatingCountry = {
  code: string;
  name: string;
  documents: number;
  prompt: {
    id: string;
    version: number;
    isActive: boolean;
    updatedAt: string;
    lastTestedAt: string | null;
    promptLength: number;
    hasGuidelines: boolean;
    model: string | null;
    gradeScale: 'LETTER' | 'MICUS';
  } | null;
};

export default function RatingsPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi<{ countries: RatingCountry[] }>('/admin/ratings');
  const [q, setQ] = useState('');
  const countries = data?.countries || [];
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return countries.filter((c) => !t || c.name.toLowerCase().includes(t) || c.code.toLowerCase().includes(t));
  }, [countries, q]);
  const configured = countries.filter((c) => c.prompt?.isActive).length;

  return (
    <div>
      <PageHeader
        title="Ratings"
        description="Versioned rating prompts per country. Save a draft, test it, then promote to canary or live — new ratings follow the live pointer, not the last edit."
        actions={
          <Link to="/documents/new" className="mz-btn-ghost">
            + Add country
          </Link>
        }
      />

      <div className="mz-fade-up grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label="Countries" value={countries.length} />
        <StatCard label="Active prompts" value={configured} accent />
        <StatCard label="Using default prompt" value={countries.length - configured} hint="Rated with the Micus default until configured" />
      </div>

      <Card className="mz-fade-up mz-d1 mt-4" padded={false}>
        <div className="border-b border-[#fbeef4] p-3">
          <SearchInput value={q} onChange={setQ} placeholder="Search country…" className="max-w-sm" />
        </div>
        {error && (
          <div className="px-3 pt-3">
            <Notice tone="error">{error}</Notice>
          </div>
        )}
        {loading && !data ? (
          <LoadingRows />
        ) : shown.length === 0 ? (
          <EmptyState
            title={countries.length ? 'No country matches' : 'No countries yet'}
            description="Ratings are configured per country. Add a country's document checklist first."
            action={
              <Link to="/documents/new" className="mz-btn-primary">
                + Add country
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="mz-table min-w-[760px]">
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Model</th>
                  <th>Scale</th>
                  <th>Documents</th>
                  <th>Last tested</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.code} className="mz-row-link" onClick={() => navigate(`/ratings/${c.code}`)}>
                    <td>
                      <div className="flex items-center gap-2">
                        <CodeChip code={c.code} />
                        <span className="font-semibold">{c.name}</span>
                      </div>
                    </td>
                    <td>
                      {!c.prompt ? (
                        <StatusPill label="Default" tone="amber" />
                      ) : c.prompt.isActive ? (
                        <StatusPill label={`Active · v${c.prompt.version}`} tone="green" />
                      ) : (
                        <StatusPill label="Paused" tone="slate" />
                      )}
                    </td>
                    <td className="text-xs text-[#9a6b82]">{c.prompt?.model || 'Default model'}</td>
                    <td className="text-xs text-[#9a6b82]">{c.prompt?.gradeScale === 'MICUS' ? 'Micus (M-AAA…)' : 'Letter (A+…D)'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Link to={`/documents/${c.code}`} className="font-semibold text-[#d6006e] hover:underline">
                        {c.documents}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap text-xs text-[#9a6b82]">{c.prompt?.lastTestedAt ? ago(c.prompt.lastTestedAt) : '—'}</td>
                    <td className="whitespace-nowrap text-xs text-[#9a6b82]">{c.prompt ? ago(c.prompt.updatedAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
